import { useUser } from "../contexts/UserContext";

/**
 * Convenience hook for product-access decisions.
 *
 * Returns:
 *   - accessType: "full" | "octopush_x" | "recording_studio" | "none" | null (while loading)
 *   - hasOctopushX / hasRecordingStudio: per-user booleans (company license + user flag)
 *   - companyHasOctopushX / companyHasRecordingStudio: company-level license presence
 *   - seats: { octopushX: {used, max}, recordingStudio: {used, max} }
 *   - loading, error, refresh
 */
export function useLicense() {
  const { license, licenseLoading, licenseError, refreshLicense, currentUser } =
    useUser();

  const accessType = license?.accessType ?? null;
  const isSuperAdmin = currentUser?.role === "super_admin";

  const userHasX = !!license?.user?.hasOctopushXAccess || isSuperAdmin;
  const userHasRS = !!license?.user?.hasRecordingStudioAccess || isSuperAdmin;

  const companyHasX = !!license?.company?.octopushX;
  const companyHasRS = !!license?.company?.recordingStudio;

  return {
    loading: licenseLoading,
    error: licenseError,
    refresh: refreshLicense,

    accessType, // "full" | "octopush_x" | "recording_studio" | "none" | null

    // Effective access = user flag AND company has a license for it
    hasOctopushX: userHasX && companyHasX,
    hasRecordingStudio: userHasRS && companyHasRS,

    companyHasOctopushX: companyHasX,
    companyHasRecordingStudio: companyHasRS,

    seats: {
      octopushX: license?.company?.octopushX
        ? {
            used: license.company.octopushX.assignedUsers,
            max: license.company.octopushX.maxUsers,
            available:
              license.company.octopushX.maxUsers -
              license.company.octopushX.assignedUsers,
          }
        : null,
      recordingStudio: license?.company?.recordingStudio
        ? {
            used: license.company.recordingStudio.assignedUsers,
            max: license.company.recordingStudio.maxUsers,
            available:
              license.company.recordingStudio.maxUsers -
              license.company.recordingStudio.assignedUsers,
          }
        : null,
    },

    tier: license?.company?.recordingStudio?.tier || null,
  };
}
